import * as Sentry from "@sentry/nuxt";

Sentry.init({
  dsn: "https://a019a8b50951a0da4ff6a996e657669c@o4507766857269248.ingest.us.sentry.io/4507766860218368",
  // Sample 10% of transactions. At 1.0 every page load shipped a full tracing
  // payload from the client, adding main-thread and network overhead on the
  // critical path (and burning quota); 0.1 keeps representative perf data at a
  // fraction of the cost. Raise temporarily if you need denser tracing.
  tracesSampleRate: 0.1,

  // Errors we deliberately drop before they reach Sentry.
  ignoreErrors: [
    // Cloudflare Turnstile client-side errors (codes like 300010 / 600010 /
    // 110600). They are thrown from Cloudflare's own api.js message handler and
    // surface here only because Sentry wraps event listeners. The widget retries
    // automatically and the user flow self-recovers, so they are pure noise —
    // we also handle them in-app via the widget's error-callback. See
    // pages/index.vue (turnstileOptions).
    /\[Cloudflare Turnstile\]/i,
    "TurnstileError",
    // Opaque cross-origin script errors (no stack, no actionable detail). These
    // come from third-party scripts (Turnstile, Clarity, GA, Rocket Loader) and
    // are unactionable. Remove this line if you need to debug a specific one.
    "Script error.",
  ],

  // Scripts Cloudflare injects into the response at the edge — none of this is
  // our code and none of it is shipped from this repo, so a throw inside it is
  // never actionable here:
  //   /cdn-cgi/zaraz/*            Zaraz tag manager (throws SyntaxError from its
  //                               own response.json() when a tool endpoint
  //                               answers with a non-JSON body — Safari surfaces
  //                               it as an unhandled rejection)
  //   /cdn-cgi/scripts/*          Rocket Loader
  //   /cdn-cgi/challenge-platform Turnstile / bot-management challenge
  // denyUrls matches the deepest frame with a real filename, which for these is
  // the Cloudflare script itself.
  denyUrls: [/\/cdn-cgi\//],

  beforeSend(event, hint) {
    const err = hint?.originalException as any;
    const message =
      typeof err === "string" ? err : err?.value ?? err?.message ?? "";

    // Belt-and-suspenders: drop any TurnstileError that slips past ignoreErrors
    // (e.g. re-thrown or wrapped so the message no longer matches above).
    if (err?.name === "TurnstileError" || /\[Cloudflare Turnstile\]/i.test(String(message))) {
      return null;
    }

    // denyUrls only inspects a single frame. Also drop anything whose stack
    // lives *entirely* in Cloudflare-injected scripts — that covers stacks
    // where the CF frame isn't the one denyUrls happens to pick (Rocket Loader
    // wrapping a listener, a rejection with a truncated stack). Any event with
    // even one frame of our own code still gets through.
    const frames = event.exception?.values?.flatMap((v) => v.stacktrace?.frames ?? []) ?? [];
    const named = frames.filter((f) => f.filename && f.filename !== "[native code]" && f.filename !== "<anonymous>");
    if (named.length > 0 && named.every((f) => f.filename!.includes("/cdn-cgi/"))) {
      return null;
    }

    return event;
  },
});
